<template>
  <div class="similar-problems-list">
    <!-- Display similar problems -->
    <div class="similar-problems-content f-w f-h">
      <div class="spc-title">
        <h2 class="basic-sub-title">
          {{ $t('message.SIMILAR_PBS') }}
        </h2>
        <div class="loading-hint">
          <button v-if="!loadingSimilar" class="add-button" @click="refresh">
            <Icon>{{ refreshMedium }}</Icon>
            <span>{{ $t('message.REFRESH') }}</span>
          </button>
          <button v-else class="add-button" @click="cancel">
            <Icon>{{ closeMedium }}</Icon>
            <span>{{ $t('message.CANCEL') }}</span>
          </button>
          <div class="field-hint">
            {{ $t('message.COMPARING_NB_OF_TOTAL', { nb: problemsComparedCount, total: totalCount }) }}
          </div>
        </div>
      </div>
      <div class="f-w spc-head">
        <div class="duplicate">
          {{ $t('message.RECURRENCE_DUPLICATE') }}
        </div>
        <div class="related">
          {{ $t('message.RELATED') }}
        </div>
        <div class="problem" :class="{ expanded: quickFiltersItem.expanded}">
          <!-- Quick filters radio buttons -->
          <div class="display-quick-filters v-centerer" @click="itemExpand(quickFiltersItem)">
            <img src="@/assets/img/arrow-bot-bold-blue.png" alt="">
            <span class="legend ml10">{{ $t('message.DISPLAY_QUICK_FILTERS') }}</span>
          </div>
          <div :id="quickFiltersItem.id" class="quick-filters">
            <div v-for="filter in quickFilters" :key="filter.title">
              <span class="legend">
                {{ filter.title }}
              </span>
              <RadioGroup v-model="filter.value" :items="filter.items" />
            </div>
          </div>
        </div>
        <div class="compare" />
      </div>
      <div v-if="loadingSimilar && allProblems.length === 0" class="loading">
        {{ $t('message.LOADING_DOTS') }}
      </div>
      <div v-else-if="!loadingSimilar && allProblems.length === 0">
        {{ $t('message.NO_PB_FOUND') }}
      </div>
      <div v-else class="spc-list">
        <transition-group name="animated-list">
          <!-- List of problems -->
          <SimilarProblemItem
            v-for="pb in sortedProblems"
            :key="pb.id"
            class="animated-list-item"
            :problem="pb"
            :compared-to="problem"
            :compared-recurrence-ids="recurrenceIds"
            @tick="problemLinkTicked"
            @displaySheet="displaySheet(pb.id)"
            @compare="compare(pb)"
          />
        </transition-group>
      </div>
    </div>
    <!-- Display another problem in overlay -->
    <transition name="fade" mode="out-in">
      <div
        v-if="showProblemSheet && sheetPbId > 0"
        class="dark-overlay v-centerer"
        @click="showProblemSheet = false"
      >
        <div class="dark-overlay-content large" @click.stop="">
          <ProblemSheetModal ref="sheet" :prop-id="sheetPbId" in-popup />
          <CloseButton @click.native="showProblemSheet = false" />
        </div>
      </div>
    </transition>
    <transition name="fade" mode="out-in">
      <ProblemsComparator
        v-if="comparedTo !== null"
        :base="problem"
        :to-compare="comparedTo"
        @pictureClick="(p, idx) => $emit('pictureClick', p, idx)"
        @close="comparedTo = null"
        @sheetEdit="(field, val) => $emit('sheetEdit', field, val)"
      />
    </transition>
  </div>
</template>

<script>
/**
 * List of SimilarProblemItem with displaySheet and compare options
 *
 * @vue-event {String, *} sheetEdit - Trasmitted from ProblemDataSection
 * @vue-event {Object} pictureClick - Trasmitted from ProblemDataSection
 */

// Components
import CloseButton from '@/components/close-button.vue';
import Icon from '@/components/icon.vue';
import ProblemsComparator from '@/components/problem-sheet/problems-comparator.vue';
import RadioGroup from '@/components/radio-group.vue';
import SimilarProblemItem from '@/components/problem-sheet/similar-problem-item.vue';
// Utils
import { itemExpand } from '@/utils/expandable';
import { closeMedium, refreshMedium } from '@/assets/icons';

export default {

  name: 'SimilarProblemsList',

  components: {
    CloseButton,
    Icon,
    ProblemsComparator,
    ProblemSheetModal: () => import('@/components/edition-modals/problem-sheet-modal.vue'),
    RadioGroup,
    SimilarProblemItem,
  },

  props: {
    problem: {
      type: Object,
      required: true,
    },
    recurrenceIds: {
      type: Array,
      default: () => [],
    },
    linkIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      refreshMedium,
      closeMedium,
      loadingSimilar: false,
      allProblems: [],
      problemsLinked: [],
      links: [],
      sheetPbId: 0,
      showProblemSheet: false,
      // As of v0.3.52, problems will be fetched by groups of 20
      // Before anything else, the component will fetch problems already linked
      linkedProblemsFetched: false,
      stopFetchNext: false,
      problemsComparedCount: 0,
      totalCount: 0,
      pbCountPerXHR: 20,
      timeoutNext: null,
      destroyed: false,
      // When comparing
      comparedTo: null,
      quickFiltersItem: { id: 'rec-field-quick-filters', expanded: false },
      quickFilters: [
        {
          title: this.$t('message.TEAM'),
          value: 0,
          field: 'affectTeam',
          compareTo: () => this.App.user.id,
          items: [
            { id: 0, label: this.$t('message.ALL') },
            { id: 1, label: this.$t('message.QFILTER_MY_TEAM') },
            { id: 2, label: this.$t('message.QFILTER_OTHER_TEAMS') },
          ],
        },
        {
          title: this.$t('message.STATE'),
          value: 0,
          field: 'state',
          compareTo: 4,
          items: [
            { id: 0, label: this.$t('message.ALL') },
            { id: 1, label: this.$t('message.QFILTER_RESOLVED') },
            { id: 2, label: this.$t('message.QFILTER_NOT_RESOLVED') },
          ],
        },
        {
          title: this.$t('message.ARCHIVES'),
          value: 0,
          field: 'archived',
          compareTo: 1,
          items: [
            { id: 0, label: this.$t('message.ALL') },
            { id: 1, label: this.$t('message.QFILTER_ARCHIVED') },
            { id: 2, label: this.$t('message.QFILTER_NOT_ARCHIVED') },
          ],
        },
      ],
    };
  },

  computed: {
    /** All similar problems fetched sorted as: user-picked, duplicates then related  */
    sortedProblems() {
      let problems = this.allProblems;
      this.quickFilters.forEach((filter) => {
        const compareTo = typeof filter.compareTo === 'function' ? filter.compareTo() : filter.compareTo;
        if (filter.value === 1) {
          problems = problems.filter(pb => pb[filter.field] === compareTo);
        } else if (filter.value === 2) {
          problems = problems.filter(pb => pb[filter.field] !== compareTo);
        }
      });
      const { pId } = this;
      return problems.sort((p1, p2) => {
        if (p1.recurrenceIds.includes(pId) || p2.recurrenceIds.includes(pId)) {
          // Duplicates first
          if (p1.recurrenceIds.includes(pId) && !p2.recurrenceIds.includes(pId)) {
            return -1;
          }
          if (p2.recurrenceIds.includes(pId) && !p1.recurrenceIds.includes(pId)) {
            return 1;
          }
        } else {
          // Related
          if (p1.linkIds.includes(pId) && !p2.linkIds.includes(pId)) {
            return -1;
          }
          if (p2.linkIds.includes(pId) && !p1.linkIds.includes(pId)) {
            return 1;
          }
        }
        return p2.similarity - p1.similarity;
      });
    },
    /** Array: problems that are not ticked as identical nor similar */
    nonLinkedProblems() {
      return this.allProblems.filter(p => !p.recurrenceIds.includes(this.pId) && !p.linkIds.includes(this.pId));
    },
    /** Number: ID of the base problem */
    pId() {
      return Number(this.problem.id);
    },
    /** Object: Words that should be excluded from the similarity algorithm */
    excludeWords() {
      return {
        lWhere: `${this.$t('message.GW')} ${this.$t('message.GW_STEP')} ${this.$t('message.GW_CHECKLIST_ITEM')}`,
      };
    },
    /** Object: Params of the XHR used to fetch next problems */
    fetchParams() {
      const params = {
        id: this.problem.id,
        locale: this.$t('min'),
        excludeWords: this.excludeWords,
      };
      if (this.linkedProblemsFetched) {
        params.limit = {
          offset: this.problemsComparedCount,
          count: this.pbCountPerXHR,
        };
      }
      return params;
    },
  },

  created() {
    this.refresh();
  },

  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.timeoutNext);
  },

  methods: {
    refresh() {
      this.problemsComparedCount = 0;
      this.linkedProblemsFetched = false;
      this.stopFetchNext = false;
      this.loadingSimilar = true;
      this.allProblems = [];
      clearTimeout(this.timeoutNext);
      this.$nextTick(this.fetchNextProblems);
    },
    fetchNextProblems() {
      if (this.destroyed) return;
      this.App.post('/server/problems/links/getSimilarProblems.php', this.fetchParams, (d) => {
        if (d instanceof Object && d.problems) {
          this.allProblems.push(...d.problems);
          // Stop conditions
          if (this.stopFetchNext) {
            this.loadingSimilar = false;
            return;
          }
          if (this.linkedProblemsFetched) {
            this.totalCount = d.totalCount;
            this.problemsComparedCount += d.problemsComparedCount;
            if (d.problemsComparedCount < this.pbCountPerXHR) {
              this.loadingSimilar = false;
            } else {
              this.timeoutNext = setTimeout(this.fetchNextProblems, 1);
            }
          } else {
            this.linkedProblemsFetched = true;
            this.timeoutNext = setTimeout(this.fetchNextProblems, 1);
          }
        }
      });
    },
    cancel() {
      this.stopFetchNext = true;
      clearTimeout(this.timeoutNext);
    },
    displaySheet(pbId) {
      this.sheetPbId = Number(pbId);
      this.showProblemSheet = true;
    },
    /** One of the fetched similar problems has been ticked => update links */
    problemLinkTicked() {
      this.$emit('tick');
    },
    /** Link IDs and recurrenc IDs have been updated */
    linksUpdated() {
      // update duplicate problems
      this.allProblems.forEach((pb) => {
        if (this.recurrenceIds.includes(pb.id)) {
          pb.recurrenceIds = this.recurrenceIds.filter(id => id !== pb.id);
          pb.recurrenceIds.push(this.problem.id);
        } else if (pb.recurrenceIds.includes(this.problem.id)) {
          pb.recurrenceIds.splice(pb.recurrenceIds.indexOf(this.problem.id));
        }
      });
    },
    compare(pb) {
      this.comparedTo = pb;
    },
    itemExpand,
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.similar-problems-list {
  height: 100%;

  .similar-problems-content {
    height: 100%;
    display: flex;
    flex-flow: column;

    .loading {
      text-align: center;
    }
    .spc-title {
      position: relative;
      text-align: center;
      margin-top: 2rem;

      h2.basic-sub-title {
        display: inline-block;
      }
      .loading-hint {
        position: absolute;
        bottom: 0px;
        right: 1em;
        text-align: right;

        .add-button {
          padding: 2px 5px;
          display: inline-flex;

          &:hover {
            background: $color-hoverbtn-light-blue;
          }
        }
      }
    }
  }
  .spc-head {
    margin-bottom: 5px;

    > * {
      display: inline-block;
      vertical-align: top;
      white-space: nowrap;
    }
    > .duplicate, > .related {
      transform: rotate(-55deg);
    }

    .duplicate, .related {
      width: 30px;
    }
    .duplicate {
      margin-left: 10px;
    }
    .related {
      margin-right: 10px;
    }
    .compare {
      width: 40px;
    }
    .problem {
      width: calc(100% - 120px);
      white-space: initial;
      text-align: left;
      padding: 4px 8px;
      box-sizing: border-box;

      &.expanded {
        background: #dfe2ff;
        .display-quick-filters img {
          transform: initial;
        }
      }
      .display-quick-filters {
        cursor: pointer;

        img {
          display: block;
          height: 1.2em;
          transform: rotate(-90deg);
          transition: transform 0.2s;
        }
      }
      .quick-filters {
        height: 0px;
        overflow: hidden;

        > div {
          display: inline-block;
          margin-right: 15px;
        }
      }
    }
  }
  .spc-list {
    flex-grow: 1;
    overflow: hidden auto;
  }
}
</style>
